import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const JQUERY_NAMESPACE = ".creditExpirationEditor"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the inline "click a date, edit it, Update/Cancel" workflow
// that only the admin org page renders (`editableValue`, from
// `@organization.allow_expiration_date_update?`).
//
// The subdomain mount renders plain expiration-date text with none of this controller's target
// markup, so `editableValue: false` has nothing to gate beyond skipping the "draw.dt" binding --
// AccountActionDecorator#expires_at_label (the only source of .expiration-date-value /
// .update-expiration-date / .cancel-expiration-date-update / .expiration-date-field markup) is
// only ever called when the page is editable, so its data-action wiring self-gates with the
// markup that carries it (pattern 21) and needs no editableValue check of its own.
//
// "draw.dt" is a jQuery-namespaced event DataTables triggers only through jQuery's own event
// system (same shape as P27's select2 "change"), so it alone stays hand-wired and survives
// DataTables replacing the row markup on every redraw the way a data-action attribute on the row
// itself already does for the four handlers below (pattern 10). bootstrap-datepicker has no
// delegated form, so it alone is re-initialized on each "draw.dt".
export default class extends Controller {
  static get targets() { return ["table"] }
  static get values() { return { editable: Boolean } }

  connect() {
    if (!this.editableValue) { return }

    this.onDraw = this.onDraw.bind(this)
    $(this.tableTarget).on(`draw.dt${JQUERY_NAMESPACE}`, this.onDraw)
  }

  disconnect() {
    if (!this.editableValue) { return }

    $(this.tableTarget).off(JQUERY_NAMESPACE)
  }

  // Re-run on every draw so newly rendered rows get a datepicker; the plugin call is a no-op on a
  // field that already has an instance.
  onDraw() {
    $(this.element).find(".expiration-date-field").each((_, field) => $(field).datepicker())
  }

  toggleUpdateButton(event) {
    event.currentTarget.parentNode.querySelector(".update-expiration-date").disabled = event.currentTarget.value === ""
  }

  displayEditForm(event) {
    // `.expiration-date-form-container` carries a non-`!important` `display: none` rule
    // (admin/account_actions.scss); jQuery 3's `.show()` writes the tag's own default display
    // back in explicitly rather than clearing the inline style, so this does too (pattern 11).
    // `.expiration-date-value` has no competing stylesheet rule, so clearing its inline style is
    // enough to restore it.
    event.currentTarget.parentElement.querySelector(".expiration-date-form-container").style.display = "block"
    event.currentTarget.style.display = "none"
  }

  hideEditForm(event) {
    const parentContainer = event.currentTarget.parentElement.parentElement

    parentContainer.querySelector(".expiration-date-value").style.display = ""
    parentContainer.querySelector(".expiration-date-form-container").style.display = "none"
  }

  updateExpirationDate(event) {
    const container = event.currentTarget.parentElement
    const dateField = container.querySelector(".expiration-date-field")
    const expirationDate = dateField.value
    // `ids` and `url` are plain strings server-side (STRING_AGG / a Rails path helper), not
    // JSON-shaped, so `.dataset` reads the same value jQuery's `.data()` coercion did (pattern 6).
    const ids = container.dataset.ids
    const url = container.dataset.url
    const updateButton = container.querySelector(".update-expiration-date")
    const cancelButton = container.querySelector(".cancel-expiration-date-update")

    // Validate that the date is in MM/DD/YYYY format with 4-digit year.
    // Only accept format: MM/DD/YYYY (reject all other formats)
    if (!expirationDate.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      window.ltAlert({
        message: "Invalid date format. Only MM/DD/YYYY format with a 4-digit year is allowed.",
        type: "toast-error",
        lasso: false
      })
      return
    }

    // Disable buttons and input field during the request
    updateButton.disabled = true
    cancelButton.disabled = true
    dateField.disabled = true

    // JSON content type is load-bearing: `bulk_update_params` reads a flat `expiration_date`/`ids`
    // body, which only parses at all once Rails knows the body is JSON.
    const options = RequestHelper.defaultPutOptions()
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" })
    options.body = JSON.stringify({ ids: ids, expiration_date: expirationDate })

    fetch(url, options).
      then((response) => {
        if (response.ok) { return null }

        return response.json().then((body) => {
          throw new Error((body && body.error) || "There was an error when updating")
        })
      }).
      then(() => {
        window.ltAlert({
          message: "Expiration date updated successfully!",
          type: "toast-success",
          duration: 2000,
          lasso: false
        })

        $(this.tableTarget).DataTable().ajax.reload(null, false)
      }).
      catch((error) => {
        window.ltAlert({ message: error.message, type: "toast-error", lasso: false })

        // Re-enable buttons and input field on error
        updateButton.disabled = false
        cancelButton.disabled = false
        dateField.disabled = false
      })
  }
}
